import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { huntingApi } from '../services/api';
import { formatForecastTime, timeToMinutes } from '../services/forecastTime';

interface HourMetrics {
  temperatureF: number;
  windSpeedMph: number;
  windGustMph: number;
  cloudPercent: number;
  precipInches: number;
  pressureInHg: number;
}

interface HourScore {
  hour: number;
  time: string;
  rating: number;
  weightedScore: number;
  metrics?: HourMetrics;
}

interface DayConditions {
  morningTempF: number;
  eveningTempF: number;
  windSpeedMph: number;
  windGustMph: number;
  cloudPercent: number;
  precipitationType: string;
  pressureTrend: string;
  frontType: string;
  lunarIllumination: number;
  moonOverhead: string | null;
  moonUnderfoot: string | null;
  lunarFeedingDawn: number;
  lunarFeedingDusk: number;
}

interface DayFactorScores {
  temperature: number;
  front: number;
  wind: number;
  precipitation: number;
  clouds: number;
  pressure: number;
  moon: number;
  lunarFeeding: number;
}

interface DailyHourlyOutlook {
  zipcode: string;
  date: string;
  dayOfWeek: string;
  sunrise: string;
  sunset: string;
  lunarPhase: string;
  hours: HourScore[];
  conditions: DayConditions;
  factors: DayFactorScores;
  summary: string;
  hasWeatherData: boolean;
}

// Overlay metrics selectable via radios. `key` maps into HourMetrics.
// min/max define a FIXED y-axis domain per metric so the line's shape is
// comparable day to day (values above max clip at the top edge).
type MetricKey = keyof HourMetrics;
const METRIC_OPTIONS: {
  key: MetricKey;
  label: string;
  unit: string;
  /** Decimal places for axis/tooltip display. */
  decimals: number;
  min: number;
  max: number;
}[] = [
  { key: 'temperatureF', label: 'Temp', unit: '°F', decimals: 0, min: -10, max: 100 },
  { key: 'windSpeedMph', label: 'Wind', unit: 'mph', decimals: 0, min: 0, max: 30 },
  { key: 'windGustMph', label: 'Gust', unit: 'mph', decimals: 0, min: 0, max: 45 },
  { key: 'cloudPercent', label: 'Cloud', unit: '%', decimals: 0, min: 0, max: 100 },
  { key: 'precipInches', label: 'Precip', unit: 'in', decimals: 2, min: 0, max: 0.25 },
  { key: 'pressureInHg', label: 'Baro', unit: 'inHg', decimals: 2, min: 28, max: 31 },
];

const FACTOR_INFO: { [key: string]: { label: string; icon: string } } = {
  temperature: { label: 'Temperature', icon: '🌡️' },
  front: { label: 'Weather Front', icon: '🌊' },
  wind: { label: 'Wind', icon: '💨' },
  precipitation: { label: 'Precipitation', icon: '🌧️' },
  clouds: { label: 'Cloud Cover', icon: '☁️' },
  pressure: { label: 'Barometric', icon: '📊' },
  moon: { label: 'Moon Phase', icon: '🌙' },
  lunarFeeding: { label: 'Lunar Feeding', icon: '🌾' },
};

function getToday(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getBarColor(score: number): string {
  if (score <= 0) return '#000000';
  if (score <= 1) return lerpColor('#000000', '#d32f2f', score);
  if (score <= 2.5) return lerpColor('#d32f2f', '#e67e22', (score - 1) / 1.5);
  if (score <= 3) return lerpColor('#e67e22', '#f1c40f', (score - 2.5) / 0.5);
  if (score <= 4) return lerpColor('#f1c40f', '#66bb6a', score - 3);
  if (score <= 5) return lerpColor('#66bb6a', '#2e7d32', score - 4);
  return '#2e7d32';
}

function lerpColor(a: string, b: string, t: number): string {
  const ar = parseInt(a.slice(1, 3), 16), ag = parseInt(a.slice(3, 5), 16), ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16), bg = parseInt(b.slice(3, 5), 16), bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

function formatPrecip(type: string): string {
  if (type === 'none') return 'None';
  return type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function formatPressure(trend: string): string {
  return trend.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function formatFront(type: string): string {
  if (type === 'none') return 'None';
  return type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function getBaromArrow(trend: string): string {
  if (trend === 'rapidly-rising' || trend === 'rising') return '↑';
  if (trend === 'rapidly-falling' || trend === 'falling') return '↓';
  return '―';
}

function getBaromLabel(trend: string): string {
  if (trend === 'rapidly-rising' || trend === 'rising') return 'Rising';
  if (trend === 'rapidly-falling' || trend === 'falling') return 'Falling';
  return 'Steady';
}

/** Shared tooltip for the hourly chart: hour, score, and selected metric. */
function HourlyTooltip({
  active,
  payload,
  metricConfig,
  hasWeather,
}: {
  active?: boolean;
  payload?: any[];
  metricConfig: { key: MetricKey; label: string; unit: string; decimals: number } | null;
  hasWeather: boolean;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  const metricVal = metricConfig ? row[metricConfig.key] : undefined;

  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-time">{row.time}</div>
      <div className="chart-tooltip-score">
        Score: {row.score}/5 ({row.rating}★)
        {!hasWeather && <span className="chart-tooltip-est"> · estimate</span>}
      </div>
      {metricConfig && metricVal != null && (
        <div className="chart-tooltip-metric" style={{ color: '#ff3b30' }}>
          {metricConfig.label}: {Number(metricVal).toFixed(metricConfig.decimals)} {metricConfig.unit}
        </div>
      )}
    </div>
  );
}

function DailyViewPage() {
  const { user, activeLocation } = useAuth();
  const zipcode = activeLocation?.zipcode;
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get('date') || getToday();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [data, setData] = useState<DailyHourlyOutlook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locationName, setLocationName] = useState('');
  const locationLabel = [activeLocation?.name, locationName].filter(Boolean).join(' · ');
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('temperatureF');

  const today = getToday();
  // Weather forecast covers ~7 days; beyond that we still show lunar/solar-only
  // scores (bars are grayed out when weather data is unavailable).
  const maxDate = addDays(today, 60);
  const canGoBack = selectedDate > today;
  const canGoForward = selectedDate < maxDate;

  useEffect(() => {
    if (zipcode) {
      fetchHourly(zipcode, selectedDate);
      huntingApi.getLocation(zipcode).then(res => {
        if (res.data.city && res.data.stateAbbr) {
          setLocationName(`${res.data.city}, ${res.data.stateAbbr}`);
        }
      }).catch(() => {});
    }
  }, [zipcode, selectedDate, user?.tier]);

  const fetchHourly = async (zipcode: string, date: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await huntingApi.getHourly(zipcode, date);
      setData(response.data);
    } catch (err: any) {
      if (err.response?.data?.code === 'UPGRADE_REQUIRED') {
        setError('Free accounts can view hourly detail for the next 3 days. Upgrade to Plus to see further ahead.');
      } else {
        setError('Failed to load hourly data. Please try again.');
      }
      console.error('Hourly fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => { if (canGoBack) setSelectedDate(addDays(selectedDate, -1)); };
  const goForward = () => { if (canGoForward) setSelectedDate(addDays(selectedDate, 1)); };

  if (loading) {
    return (
      <div className="daily-view">
        <div className="loading" role="status">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Loading hourly data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const upgradeRequired = error.startsWith('Free accounts');
    return (
      <div className="daily-view">
        <div className="error-state">
          <p>{error}</p>
          {upgradeRequired ? (
            <button onClick={() => setSelectedDate(today)} className="btn btn-primary">
              Back to Today
            </button>
          ) : (
            <button onClick={() => zipcode && fetchHourly(zipcode, selectedDate)} className="btn btn-primary">
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const sunriseHour = timeToMinutes(data.sunrise) / 60;
  const sunsetHour = timeToMinutes(data.sunset) / 60;

  const metricConfig = METRIC_OPTIONS.find((m) => m.key === selectedMetric) || null;

  // Build chart rows. Each hour carries its score (for the bars), a color,
  // a dark-hour flag, and the raw metric values (for the overlay line).
  const chartData = data.hours.map((h) => ({
    hour: h.hour,
    time: formatForecastTime(h.time),
    score: h.weightedScore,
    rating: h.rating,
    isDark: h.hour < Math.floor(sunriseHour) || h.hour >= Math.ceil(sunsetHour),
    // Overall hourly score is always meaningful (lunar/solar even without
    // weather), so always color it. The per-metric line/radios below remain
    // disabled on no-weather days.
    barColor: getBarColor(h.weightedScore),
    // Metric values (undefined on no-weather hours) drive the line.
    temperatureF: h.metrics?.temperatureF,
    windSpeedMph: h.metrics?.windSpeedMph,
    windGustMph: h.metrics?.windGustMph,
    cloudPercent: h.metrics?.cloudPercent,
    precipInches: h.metrics?.precipInches,
    pressureInHg: h.metrics?.pressureInHg,
  }));

  return (
    <div className="daily-view">
      <div className="daily-card">
        <div className="daily-card-header">
          <button className="day-nav-btn" onClick={goBack} disabled={!canGoBack} aria-label="Previous day">◀</button>
          <div className="daily-card-title">
            <h2>{formatDisplayDate(selectedDate)}</h2>
            {locationLabel && <span className="daily-card-date">{locationLabel}</span>}
          </div>
          <button className="day-nav-btn" onClick={goForward} disabled={!canGoForward} aria-label="Next day">▶</button>
        </div>

        <div className="daily-meta">
          <span>🌅 {formatForecastTime(data.sunrise)}</span>
          <span>🌇 {formatForecastTime(data.sunset)}</span>
          <span>🌙 {data.lunarPhase}</span>
        </div>

        {data.summary && <p className="daily-summary">{data.summary}</p>}

        {!data.hasWeatherData && (
          <p className="daily-no-weather-note">
            Weather forecast isn't available this far out. Scores below are based on
            lunar and solar timing only and will refine as the date approaches.
          </p>
        )}

        <div className="hourly-chart">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart accessibilityLayer data={chartData} margin={{ top: 10, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#3a2c22" />
              <XAxis
                dataKey="time"
                ticks={chartData.filter((row) => row.hour % 3 === 0).map((row) => row.time)}
                interval="preserveStartEnd"
                minTickGap={12}
                tick={{ fontSize: 11, fill: '#9c8f84' }}
                axisLine={{ stroke: '#3a2c22' }}
                tickLine={false}
              />
              {/* Left axis: hunting score 1-5 (bars) */}
              <YAxis
                yAxisId="score"
                domain={[1, 5]}
                ticks={[1, 2, 3, 4, 5]}
                tick={{ fontSize: 11, fill: '#9c8f84' }}
                axisLine={{ stroke: '#3a2c22' }}
                tickLine={false}
                width={28}
              />
              {/* Right axis: selected metric in real units (only when a line is shown) */}
              {metricConfig && data.hasWeatherData && (
                <YAxis
                  yAxisId="metric"
                  orientation="right"
                  domain={[metricConfig.min, metricConfig.max]}
                  allowDataOverflow
                  tick={{ fontSize: 11, fill: '#ff3b30' }}
                  axisLine={{ stroke: '#ff3b30' }}
                  tickLine={false}
                  width={44}
                  tickFormatter={(v: number) => v.toFixed(metricConfig.decimals)}
                  label={{
                    value: `${metricConfig.label} (${metricConfig.unit})`,
                    angle: 90,
                    position: 'insideRight',
                    style: { fontSize: 11, fill: '#ff3b30' },
                  }}
                />
              )}
              <Tooltip content={<HourlyTooltip metricConfig={metricConfig} hasWeather={data.hasWeatherData} />} />
              <Bar yAxisId="score" dataKey="score" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                {chartData.map((d) => (
                  <Cell key={d.hour} fill={d.barColor} fillOpacity={d.isDark ? 0.55 : 1} />
                ))}
              </Bar>
              {metricConfig && data.hasWeatherData && (
                <Line
                  yAxisId="metric"
                  type="monotone"
                  dataKey={metricConfig.key}
                  stroke="#ff3b30"
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          <table className="sr-only">
            <caption>Hourly hunting scores{metricConfig && data.hasWeatherData ? ` with ${metricConfig.label.toLowerCase()}` : ''}</caption>
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Score (out of 5)</th>
                {metricConfig && data.hasWeatherData && <th scope="col">{metricConfig.label} ({metricConfig.unit})</th>}
              </tr>
            </thead>
            <tbody>
              {chartData.map((d) => {
                const metricVal = metricConfig ? d[metricConfig.key] : undefined;
                return (
                  <tr key={d.hour}>
                    <th scope="row">{d.time}</th>
                    <td>{d.score}</td>
                    {metricConfig && data.hasWeatherData && (
                      <td>{metricVal != null ? Number(metricVal).toFixed(metricConfig.decimals) : '—'}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Metric overlay selector (single-select). Disabled with no weather. */}
          <div className="metric-radios" role="radiogroup" aria-label="Overlay metric">
            {METRIC_OPTIONS.map((m) => (
              <label
                key={m.key}
                className={`metric-radio ${selectedMetric === m.key ? 'metric-radio-active' : ''} ${!data.hasWeatherData ? 'metric-radio-disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="overlay-metric"
                  checked={selectedMetric === m.key}
                  disabled={!data.hasWeatherData}
                  onChange={() => setSelectedMetric(m.key)}
                />
                {m.label}
              </label>
            ))}
          </div>
        </div>

        {/* Condition bubbles */}
        <div className="daily-conditions">
          <div className="condition-chip">
            <span className="condition-label">AM Temp</span>
            <span className="condition-value">{data.conditions.morningTempF}°F</span>
          </div>
          <div className="condition-chip">
            <span className="condition-label">PM Temp</span>
            <span className="condition-value">{data.conditions.eveningTempF}°F</span>
          </div>
          <div className="condition-chip">
            <span className="condition-label">Wind</span>
            <span className="condition-value">{data.conditions.windSpeedMph} mph</span>
          </div>
          <div className="condition-chip">
            <span className="condition-label">Gusts</span>
            <span className="condition-value">{data.conditions.windGustMph} mph</span>
          </div>
          <div className="condition-chip">
            <span className="condition-label">Clouds</span>
            <span className="condition-value">{data.conditions.cloudPercent}%</span>
          </div>
          <div className="condition-chip">
            <span className="condition-label">Baro</span>
            <span className="condition-value">
              <span aria-hidden="true">{getBaromArrow(data.conditions.pressureTrend)}</span>
              <span className="sr-only">{getBaromLabel(data.conditions.pressureTrend)}</span>
            </span>
          </div>
          <div className="condition-chip">
            <span className="condition-label">Moon</span>
            <span className="condition-value">{Math.round(data.conditions.lunarIllumination * 100)}%</span>
          </div>
          {data.conditions.moonOverhead && (
            <div className="condition-chip" title="Moon overhead (upper transit)">
              <span className="condition-label"><span aria-hidden="true">🌙↑</span><span className="sr-only">Moon overhead</span></span>
              <span className="condition-value">{formatForecastTime(data.conditions.moonOverhead)}</span>
            </div>
          )}
          {data.conditions.moonUnderfoot && (
            <div className="condition-chip" title="Moon underfoot (lower transit)">
              <span className="condition-label"><span aria-hidden="true">🌙↓</span><span className="sr-only">Moon underfoot</span></span>
              <span className="condition-value">{formatForecastTime(data.conditions.moonUnderfoot)}</span>
            </div>
          )}

          {/* Call-out chips (specific positive/negative factors) come last */}
          {data.conditions.precipitationType !== 'none' && (
            <div className="condition-chip condition-chip-alert">
              <span className="condition-label">Precip</span>
              <span className="condition-value">{formatPrecip(data.conditions.precipitationType)}</span>
            </div>
          )}
          {data.conditions.frontType !== 'none' && (
            <div className="condition-chip condition-chip-highlight">
              <span className="condition-label">Front</span>
              <span className="condition-value">{formatFront(data.conditions.frontType)}</span>
            </div>
          )}
        </div>

        {/* Factor scores. Weights are intentionally not shown (proprietary). */}
        <div className="daily-factors">
          <h4 className="daily-factors-title">Factor Scores</h4>
          <div className="daily-factor-list">
            {Object.entries(data.factors)
              // Lunar Feeding is shown as two dedicated dawn/dusk bars below.
              .filter(([key]) => key !== 'lunarFeeding')
              .map(([key, score]) => {
                const info = FACTOR_INFO[key] || { label: key, icon: '•' };
                const barColor = getBarColor(score as number);
                return (
                  <div key={key} className="daily-factor-row">
                    <span className="daily-factor-icon" aria-hidden="true">{info.icon}</span>
                    <span className="daily-factor-label">{info.label}</span>
                    <div className="daily-factor-bar-track">
                      <div className="daily-factor-bar-fill" style={{ width: `${((score as number) / 5) * 100}%`, backgroundColor: barColor }} />
                    </div>
                    <span className="daily-factor-score">{(score as number).toFixed(1)}</span>
                  </div>
                );
              })}

            {/* Dawn/Dusk hunt scores driven by lunar feeding timing.
                Weight display intentionally omitted (kept proprietary). */}
            <div className="daily-factor-row">
              <span className="daily-factor-icon" aria-hidden="true">🌾</span>
              <span className="daily-factor-label">Dawn Hunt</span>
              <div className="daily-factor-bar-track">
                <div className="daily-factor-bar-fill" style={{ width: `${(data.conditions.lunarFeedingDawn / 5) * 100}%`, backgroundColor: getBarColor(data.conditions.lunarFeedingDawn) }} />
              </div>
              <span className="daily-factor-score">{data.conditions.lunarFeedingDawn.toFixed(1)}</span>
            </div>
            <div className="daily-factor-row">
              <span className="daily-factor-icon" aria-hidden="true">🌾</span>
              <span className="daily-factor-label">Dusk Hunt</span>
              <div className="daily-factor-bar-track">
                <div className="daily-factor-bar-fill" style={{ width: `${(data.conditions.lunarFeedingDusk / 5) * 100}%`, backgroundColor: getBarColor(data.conditions.lunarFeedingDusk) }} />
              </div>
              <span className="daily-factor-score">{data.conditions.lunarFeedingDusk.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyViewPage;
